<template>
  <OverlayPanel
    ref="tooltipOverlayPanel"
    class="reactive-tooltip"
  >
    <slot>{{ value }}</slot>
  </OverlayPanel>
</template>

<script setup lang="ts">
/**
 * Primevue's `v-tooltip` directive is not reactive. Updating the tooltip value while the mouse is hovered
 * will not update the displayed text. This component works around that limitation by simulating a tooltip
 * using an `OverlayPanel` that re-renders whenever its `value` prop changes.
 *
 * Usage: obtain a template ref to this component and call `toggle(event)`, `show(event)`, or `hide()`
 * from the parent to control visibility.
 */
import OverlayPanel from '@NitrexGroup/primevue/overlaypanel';
import {
  useTemplateRef,
} from 'vue';

defineProps<{
  value?: string,
}>();

const tooltipOverlayPanel = useTemplateRef<InstanceType<typeof OverlayPanel>>('tooltipOverlayPanel');

/**
 * Toggles the visibility of the overlay.
 * @param {Event} event - Browser event.
 * @param {*} [target] - Optional target if event.currentTarget should not be used.
 */
function onToggle(event: Event, target?: any): void {
  tooltipOverlayPanel.value?.toggle(event, target);
}
/**
 * Shows the overlay.
 * @param {Event} event - Browser event.
 * @param {*} [target] - Optional target if event.currentTarget should not be used.
 */
function onShow(event: Event, target?: any): void {
  tooltipOverlayPanel.value?.show(event, target);
}
/**
 * Hides the overlay.
 */
function onHide(): void {
  tooltipOverlayPanel.value?.hide();
}

defineExpose({
  toggle: onToggle,
  show: onShow,
  hide: onHide,
});
</script>

<style lang="scss">
.reactive-tooltip {
  &.p-overlaypanel {
    background: var(--surface-600);
    color: var(--surface-0);
    border: none;
    border-radius: var(--border-radius);
    box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);

    &::before {
      border-bottom-color: var(--surface-600);
    }

    &::after {
      border-bottom-color: var(--surface-600);
    }

    .p-overlaypanel-content {
      padding: 0.75rem;
      color: var(--surface-0);
    }
  }
}
</style>
